import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

export interface SwipeListing {
  id: string;
  table_name: string;
  category: string;
  title: string;
  description: string;
  price: number;
  currency: string;
  location: string;
  images: string[];
  first_image: string | null;
  plan_name: string;
  is_featured: boolean;
  created_at: string;
  view_count: number;
  has_images: boolean;
  image_count: number;
  first_name: string;
  last_name: string;
}

export interface SwipeFeedResponse {
  status: string;
  data: {
    listings: SwipeListing[];
    batch_size: number;
    has_more: boolean;
    total_viewed_in_session: number;
  };
}

export interface SwipeActionResponse {
  status: string;
  message: string;
  data: {
    action: 'left' | 'right';
    wishlist_added?: boolean;
    wishlist_id?: string;
    skipped?: boolean;
  };
}

export interface SwipeStats {
  session_viewed_count: number;
  total_wishlist_count: number;
  wishlist_by_category: {
    [category: string]: number;
  };
}

export interface SwipeStatsResponse {
  status: string;
  data: SwipeStats;
}

@Injectable({
  providedIn: 'root',
})
export class SwipeService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient, private authService: AuthService) {}

  getSwipeFeed(
    batchSize: number = 15,
    categories?: string[]
  ): Observable<SwipeFeedResponse> {
    if (!this.authService.isLoggedIn()) {
      return throwError(() => new Error('Authentication required'));
    }

    let params: any = {
      batch_size: batchSize.toString(),
    };

    if (categories && categories.length > 0) {
      params.categories = categories.join(',');
    }

    return this.http
      .get<SwipeFeedResponse>(`${this.apiUrl}/swipe/feed`, { params })
      .pipe(
        catchError((error) =>
          this.handleError(error, 'Failed to fetch swipe feed')
        )
      );
  }

  handleSwipeAction(
    listingId: string,
    tableName: string,
    action: 'left' | 'right'
  ): Observable<SwipeActionResponse> {
    if (!this.authService.isLoggedIn()) {
      return throwError(() => new Error('Authentication required'));
    }

    const requestBody = {
      listing_id: listingId,
      table_name: tableName,
      action: action,
    };

    return this.http
      .post<SwipeActionResponse>(`${this.apiUrl}/swipe/action`, requestBody)
      .pipe(
        catchError((error) =>
          this.handleError(error, 'Failed to process swipe action')
        )
      );
  }

  getSwipeStats(): Observable<SwipeStats> {
    if (!this.authService.isLoggedIn()) {
      return throwError(() => new Error('Authentication required'));
    }

    return this.http.get<SwipeStatsResponse>(`${this.apiUrl}/swipe/stats`).pipe(
      map((response) => response.data),
      catchError((error) =>
        this.handleError(error, 'Failed to fetch swipe stats')
      )
    );
  }

  resetSwipeSession(): Observable<{ success: boolean }> {
    if (!this.authService.isLoggedIn()) {
      return throwError(() => new Error('Authentication required'));
    }

    return this.http
      .delete<{ status: string; message: string }>(
        `${this.apiUrl}/swipe/session`
      )
      .pipe(
        map(() => ({ success: true })),
        catchError((error) =>
          this.handleError(error, 'Failed to reset swipe session')
        )
      );
  }

  private handleError(error: any, defaultMessage: string): Observable<never> {
    let errorMessage = defaultMessage;

    if (error.status === 401 || error.status === 403) {
      errorMessage = 'Authentication required. Please log in.';
    } else if (error.status === 404) {
      errorMessage = 'Listing not found or has been removed.';
    } else if (error.status === 409) {
      errorMessage = 'This listing is already in your wishlist.';
    } else if (error.status === 400) {
      errorMessage = error.error?.message || 'Invalid request.';
    } else if (error.error?.message) {
      errorMessage = error.error.message;
    }

    console.error('Swipe service error:', error);
    return throwError(() => new Error(errorMessage));
  }

  canUseSwipe(): boolean {
    return this.authService.isLoggedIn();
  }
}
